import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useLazyQuery} from '@apollo/client';
import {Dropdown, Empty, Form, Select, Table, Tag, Tooltip} from 'antd';
import {SelectOutlined} from '@ant-design/icons';
import {Button} from '@dabafinance/react-components';
import {toast} from 'react-toastify';
import moment from 'moment';
import {useSelector} from 'react-redux';

// Shared shell styles that give the page the same header + table container as
// the wallet transactions listing.
import shell from '../../view/swap/swapTransactions.module.scss';
import styles from '../walletTransaction.module.scss';

import backIcon from 'images/back.png';
import moreIcon from 'icons/dots.svg';
import {GET_WALLET_TRANSACTIONS_BY_WISE_TRANSFER_ID} from 'graphql/queries/wallet';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {currencySymbols} from 'utils/mock';
import {renderTransactionId} from 'utils/helper';
import {
  downloadProviderSafeTransactionReceipt,
  downloadTransactionReceipt,
  supportsProviderSafeReceipt,
} from 'utils/transactionReceipt';

import DBModal from 'components/modal/modal';
import ViewFilesModal from 'components/viewFilesModal';
import {
  buildSendNotificationMenuItem,
  useSendUserNotification,
} from 'components/sendUserNotification';

import WireTransferDetails from '../transferDetails/WireTransferDetails';
import UpdateWalletTransaction from '../UpdateWalletTransaction/UpdateWalletTransaction';
import {AutomateTransferModal} from '../AutomateTransferModal';

import ownStyles from './transferLookup.module.scss';

// Providers we can look up transferIds for. Only WISE is wired end-to-end
// today; the others are placeholders so the UI is ready when they land.
const PROVIDERS = [
  {value: 'WISE', label: 'Wise', enabled: true},
  {value: 'REVOLUT', label: 'Revolut (coming soon)', enabled: false},
];

const sourceColor = source => {
  switch (source) {
    case 'RECONCILED':
      return 'green';
    case 'EXTRACTED':
      return 'blue';
    case 'BOTH':
      return 'geekblue';
    default:
      return 'default';
  }
};

const sourceLabel = source => {
  switch (source) {
    case 'RECONCILED':
      return 'Reconciled';
    case 'EXTRACTED':
      return 'Extracted';
    case 'BOTH':
      return 'Extracted + Reconciled';
    default:
      return source;
  }
};

const TransferLookup = () => {
  const navigate = useNavigate();
  const [provider, setProvider] = useState('WISE');
  const [transferId, setTransferId] = useState('');
  const [lastSearched, setLastSearched] = useState('');
  const [updateForm] = Form.useForm();
  const [copied, setCopied] = useState(false);
  const [bankDetails, setBankDetails] = useState({});
  const [openBankModal, setOpenBankModal] = useState(false);
  const [updateModal, setUpdateModal] = useState({
    isOpen: false,
    content: null,
  });
  const [viewFilesModal, setViewFilesModal] = useState({
    isOpen: false,
    files: [],
  });
  const [automateTransferModal, setAutomateTransferModal] = useState({
    isOpen: false,
    content: null,
    type: null,
  });

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Wallet),
  );

  const {canSendNotification, openSendNotification, sendNotificationModal} =
    useSendUserNotification();

  const [runQuery, {data, loading, error, refetch}] = useLazyQuery(
    GET_WALLET_TRANSACTIONS_BY_WISE_TRANSFER_ID,
    {
      fetchPolicy: 'no-cache',
      onError: err => toast.error(err?.message || 'Lookup failed'),
    },
  );

  const result = data?.getWalletTransactionsByWiseTransferId;
  const isError = result?.__typename === 'Error';
  const hits = !isError ? result?.hits || [] : [];
  // Flatten hits to full WalletTransaction shape (with a synthetic `source`
  // attached) so the shared column defs below can consume them as-is.
  const transactions = hits
    .map(h => (h?.transaction ? {...h.transaction, _source: h.source} : null))
    .filter(Boolean);

  const submit = e => {
    e?.preventDefault?.();
    const trimmed = transferId.trim();
    if (!trimmed) {
      toast.error('Enter a transfer id');
      return;
    }
    if (provider !== 'WISE') {
      toast.error(`${provider} lookup is not yet supported`);
      return;
    }
    setLastSearched(trimmed);
    runQuery({variables: {transferId: trimmed}});
  };

  const rerun = () => {
    if (!lastSearched) return;
    runQuery({variables: {transferId: lastSearched}});
  };

  const handleCopy = referenceId => {
    if (!referenceId) return;
    navigator.clipboard.writeText(referenceId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 400);
    });
  };

  const handleDownloadReceipt = async record => {
    try {
      await downloadTransactionReceipt(record);
      toast.success('Transaction receipt downloaded');
    } catch (err) {
      toast.error('Unable to generate the transaction receipt');
    }
  };

  const handleDownloadProviderSafeReceipt = async record => {
    try {
      await downloadProviderSafeTransactionReceipt(record);
      toast.success('Provider-safe transaction receipt downloaded');
    } catch (err) {
      toast.error('Unable to generate the provider-safe transaction receipt');
    }
  };

  const handleOpenBankModal = (dataForModal, event) => {
    if (event?.target?.isConnected) {
      setOpenBankModal(true);
      setBankDetails(dataForModal);
    }
  };

  if (!tabOperations?.view) {
    return (
      <div className={ownStyles.container}>
        <div className={ownStyles.empty}>
          You don&apos;t have permission to view this page.
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: 'Source',
      dataIndex: '_source',
      key: 'source',
      width: 130,
      fixed: 'left',
      render: source => (
        <Tag color={sourceColor(source)}>{sourceLabel(source)}</Tag>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 190,
      render: (_, record) => {
        const name = `${record?.user?.firstName ?? ''} ${
          record?.user?.lastName ?? ''
        }`.trim();
        const userId = record?.user?.id;
        if (userId) {
          return (
            <a
              href={`/dashboard/user/${userId}/wallet-settings`}
              target="_blank"
              rel="noreferrer"
              className={styles.userNameLink}
              onClick={e => e.stopPropagation()}>
              {name || '-'}
            </a>
          );
        }
        return <p>{name || '-'}</p>;
      },
    },
    {
      title: 'Reference ID',
      dataIndex: 'referenceId',
      key: 'referenceId',
      render: (_, record) => (
        <Tooltip trigger="hover" title={copied ? 'Copied' : record?.referenceId}>
          <p
            onClick={e => {
              e.stopPropagation();
              handleCopy(record?.referenceId);
            }}
            className={styles.reference}>
            {renderTransactionId(record?.referenceId)}
          </p>
        </Tooltip>
      ),
    },
    {
      title: 'Provider ID',
      dataIndex: 'providerId',
      key: 'providerId',
      // Prefer the admin-stored providerTransferId (guarded providers rarely
      // carry a metadata.transactionId); fall back to the Wise automation
      // read, then the legacy provider transactionId for everything else.
      render: (_, record) => {
        const providerId =
          record?.paymentGateway?.metadata?.providerTransferId ||
          record?.paymentGateway?.metadata?.wiseExtractedTransferId ||
          record?.paymentGateway?.metadata?.transactionId;
        return (
          <Tooltip trigger="hover" title={copied ? 'Copied' : providerId}>
            <p
              onClick={e => {
                e.stopPropagation();
                handleCopy(providerId);
              }}
              className={styles.reference}>
              {renderTransactionId(providerId)}
            </p>
          </Tooltip>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (_, record) => (
        <Tag color={record?.direction === 'DEPOSIT' ? 'black' : 'default'}>
          {record?.direction}
        </Tag>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'paymentProvider',
      key: 'paymentProvider',
      render: (_, record) => (
        <span>
          {record?.paymentProvider
            ?.toLowerCase()
            ?.replace(/\b\w/g, c => c.toUpperCase())}
        </span>
      ),
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (_, record) => (
        <div
          onClick={e => {
            e.stopPropagation();
            handleOpenBankModal(
              {
                ...record?.paymentMetadata,
                email: record?.paymentGateway?.metadata?.email,
                paymentProvider: record?.paymentProvider,
              },
              e,
            );
          }}
          style={{width: 170}}>
          <span>
            {record?.paymentMetadata?.paymentMethod ? (
              <Tag
                color={
                  {
                    'WIRE_TRANSFER': 'gold',
                    'BANK_TRANSFER': 'geekblue',
                    'CRYPTO': 'orange',
                    'THIRD_PARTY_FLUTTERWAVE': 'purple',
                    'THIRD_PARTY_PAYSTACK': 'magenta',
                    'THIRD_PARTY_PAYPAL': 'cyan',
                    'THIRD_PARTY_SVB': 'volcano',
                    'THIRD_PARTY_REVOLUT': 'green',
                    'THIRD_PARTY_WISE': 'lime',
                    'MOBILE_MONEY': 'blue',
                    'HUB2_MOBILE_MONEY': 'geekblue',
                    'MAVIANCE_MOBILE_MONEY': 'orange',
                  }[record?.paymentMetadata?.paymentMethod] || 'default'
                }>
                {record?.paymentMetadata?.paymentMethod
                  .replace(/_/g, ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, c => c.toUpperCase())}
              </Tag>
            ) : (
              '-- -- --'
            )}
          </span>
          <SelectOutlined />
        </div>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalTransactionAmountIncludingFees',
      key: 'totalAmount',
      render: (_, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(
            record?.totalTransactionAmountIncludingFees || 0,
          )?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Amount (Net of Fees)',
      dataIndex: 'transactionAmountNetOfFees',
      key: 'amountNetOfFees',
      render: (_, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(record?.transactionAmountNetOfFees || 0)?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Fees',
      dataIndex: 'transactionFee',
      key: 'fees',
      render: (_, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(record?.transactionFee || 0)?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Locked Transaction',
      dataIndex: 'automated',
      key: 'automated',
      render: (_, record) => (
        <Tag color={record?.locked ? 'green' : 'red'}>
          {record?.locked ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      // Failure reason surfaces as a tooltip on the tag itself — same
      // treatment as the main WalletTransactions queue so admins don't
      // need to open the detail modal to see why a tx failed.
      render: (_, record) => {
        const reasonCode = record?.failureReasonCode;
        const reasonEN = record?.failureReasonMessages?.EN;
        const reasonFR = record?.failureReasonMessages?.FR;
        const hasReason = reasonCode || reasonEN || reasonFR;
        const tag = (
          <Tag
            color={
              {SUCCESSFUL: 'green', PENDING: 'yellow', FAILED: 'red'}[
                record?.status
              ] || 'blue'
            }
            key={record?.enabled}>
            {record?.status}
          </Tag>
        );
        if (!hasReason) return tag;
        return (
          <Tooltip
            trigger="hover"
            overlayStyle={{maxWidth: 360}}
            title={
              <span style={{whiteSpace: 'pre-line'}}>
                {reasonCode ? `Code: ${reasonCode}\n` : ''}
                {reasonEN ? `EN: ${reasonEN}` : ''}
                {reasonEN && reasonFR ? '\n' : ''}
                {reasonFR ? `FR: ${reasonFR}` : ''}
              </span>
            }>
            {tag}
          </Tooltip>
        );
      },
    },
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'time',
      render: (_, record) => (
        <p className={styles.reference}>
          {moment(record?.createdAt).format('M/D/YY hh:mm a')}
        </p>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      fixed: 'right',
      width: 80,
      key: 'action',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'download-receipt',
            label: 'Download receipt',
            onClick: async e => {
              e.domEvent.stopPropagation();
              await handleDownloadReceipt(record);
            },
          },
        ];

        if (supportsProviderSafeReceipt(record)) {
          menuItems.splice(1, 0, {
            key: 'download-provider-safe-receipt',
            label: 'Download provider-safe receipt',
            onClick: async e => {
              e.domEvent.stopPropagation();
              await handleDownloadProviderSafeReceipt(record);
            },
          });
        }

        if (record?.type === 'WITHDRAWAL') {
          menuItems.push(
            {
              key: 'automate',
              label: 'Automate Transfer',
              onClick: e => {
                e.domEvent.stopPropagation();
                setAutomateTransferModal({
                  isOpen: true,
                  content: record,
                  type: 'TRANSFER',
                });
              },
            },
            {
              key: 'transfer-status',
              label: 'Transfer Status',
              onClick: e => {
                e.domEvent.stopPropagation();
                setAutomateTransferModal({
                  isOpen: true,
                  content: record,
                  type: 'TRANSFER_STATUS',
                });
              },
            },
          );
        }

        const files = record?.paymentGateway?.metadata?.files;
        if (files && files.length > 0) {
          menuItems.push({
            key: 'view-files',
            label: 'View Files',
            onClick: e => {
              e.domEvent.stopPropagation();
              setViewFilesModal({isOpen: true, files});
            },
          });
        }

        const notifyItem = buildSendNotificationMenuItem({
          user: record?.user,
          context: {
            label: `${record?.direction ?? 'Wallet'} transaction`,
            reference: renderTransactionId(record?.referenceId),
          },
          onOpen: openSendNotification,
          allowed: canSendNotification,
        });
        if (notifyItem) menuItems.push(notifyItem);

        return (
          <Dropdown trigger={'click'} menu={{items: menuItems}}>
            <img src={moreIcon} alt="" onClick={e => e.stopPropagation()} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className={shell.page}>
      <div className={shell.header}>
        <div className={shell.titleWrap}>
          <button
            type="button"
            className={shell.backButton}
            onClick={() => navigate('/dashboard/wallet')}>
            <img
              src={backIcon}
              alt="back"
              style={{width: 14, marginRight: 6, verticalAlign: 'middle'}}
            />
            Wallet
          </button>
          <h1 className={shell.title}>Transfer ID Lookup</h1>
          <p className={shell.subtitle}>
            Pick a payment provider and enter a transfer reference number to
            see every Daba wallet transaction that has claimed it (via receipt
            extraction or automated reconciliation). Click a row to review or
            correct it.
          </p>
        </div>
      </div>

      <form className={ownStyles.searchForm} onSubmit={submit}>
        <Select
          value={provider}
          onChange={setProvider}
          style={{minWidth: 160}}
          size="large">
          {PROVIDERS.map(p => (
            <Select.Option key={p.value} value={p.value} disabled={!p.enabled}>
              {p.label}
            </Select.Option>
          ))}
        </Select>
        <input
          className={ownStyles.input}
          type="text"
          placeholder="e.g. 2378903168 or TRANSFER-2378903168"
          value={transferId}
          onChange={e => setTransferId(e.target.value)}
          autoFocus
        />
        <Button
          label={loading ? 'Searching…' : 'Search'}
          disabled={loading}
          onClick={submit}
        />
      </form>

      {error && (
        <div className={ownStyles.error}>Request error: {error.message}</div>
      )}
      {isError && (
        <div className={ownStyles.error}>
          {result?.message || 'Server returned an error'}
        </div>
      )}

      {lastSearched && !loading && !isError && result?.resultMessage && (
        <div style={{color: '#6b7280', fontSize: 13, marginBottom: 8}}>
          {result.resultMessage}
        </div>
      )}

      {lastSearched && !loading && !isError && result && (
        <div className={ownStyles.summary}>
          <div className={ownStyles.stat}>
            <div className={ownStyles.label}>Transfer ID</div>
            <div className={ownStyles.value}>{result.transferId}</div>
          </div>
          <div className={ownStyles.stat}>
            <div className={ownStyles.label}>Transactions found</div>
            <div className={ownStyles.value}>{result.count}</div>
          </div>
          <div className={ownStyles.stat}>
            <div className={ownStyles.label}>Successful credits</div>
            <div className={ownStyles.value}>
              {transactions.filter(t => t?.status === 'SUCCESSFUL').length}
            </div>
          </div>
        </div>
      )}

      <section className={`${shell.tableContainer} ${styles.scrollTable}`}>
        <Table
          rowKey="id"
          loading={loading}
          onRow={record => ({
            onClick: () => setUpdateModal({isOpen: true, content: record}),
            className: styles.tableRow,
          })}
          columns={columns}
          pagination={false}
          dataSource={transactions}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  lastSearched
                    ? `No Daba transaction has claimed transfer id ${lastSearched}.`
                    : 'Enter a Wise transfer id and hit Search.'
                }
              />
            ),
          }}
        />
      </section>

      <DBModal
        isOpen={openBankModal}
        content={
          <WireTransferDetails isOpen={openBankModal} data={bankDetails} />
        }
        handleClose={() => setOpenBankModal(false)}
      />
      <DBModal
        isOpen={updateModal.isOpen}
        destroyOnClose={true}
        handleClose={() => {
          updateForm.resetFields();
          setUpdateModal({isOpen: false, content: null});
        }}
        width={900}
        content={
          <UpdateWalletTransaction
            currency={updateModal?.content?.currency}
            id={updateModal?.content?.id}
            operation={tabOperations?.update}
            form={updateForm}
            refetch={rerun}
            closeModal={() => {
              updateForm.resetFields();
              setUpdateModal({isOpen: false, content: null});
            }}
            userId={updateModal?.content?.user?.id}
          />
        }
      />
      <DBModal
        isOpen={automateTransferModal.isOpen}
        handleClose={() => {
          setAutomateTransferModal({isOpen: false, content: null, type: null});
        }}
        width={900}
        content={
          <AutomateTransferModal
            refetch={rerun}
            content={automateTransferModal.content}
            type={automateTransferModal.type}
            onClose={() => {
              setAutomateTransferModal({
                isOpen: false,
                content: null,
                type: null,
              });
            }}
          />
        }
      />
      <ViewFilesModal
        isOpen={viewFilesModal.isOpen}
        onClose={() => setViewFilesModal({isOpen: false, files: []})}
        files={viewFilesModal.files}
      />
      {sendNotificationModal}
    </div>
  );
};

export default TransferLookup;
